import { Group as LayerGroup, Tile as TileLayer } from "ol/layer.js";
import XYZ from "ol/source/XYZ";
import * as proj from "ol/proj";
import * as extent from "ol/extent";
import WMTS from "ol/source/WMTS";
import WMTSTileGrid from "ol/tilegrid/WMTS";
import OSM from 'ol/source/OSM.js';
import ImageWMS from 'ol/source/ImageWMS.js';
import ImageLayer from "ol/layer/Image";
import { TileArcGISRest } from "ol/source";
import { ImageArcGISRest } from "ol/source";

export default class Themes {
  constructor() {
    this.pendingConfiguration = [];
    this.core = null;
    this.lastState = {}
    this.states = [];
  }

  apply(core) {
    this.core = core;

    core.mapCmd("addThemesCmd", this.addLayerCategories.bind(this));

    core.mapCmd("updateLayerCmd", this.updateLayer.bind(this));

    core.mapCmd("toggleSelectedThemesCmd", this.toggleSelectedThemes.bind(this));

    core.mapCmd("setCategoryTransparencyCmd", this.setCategoryTransparency.bind(this));

    //core.mapCmd("save", this.save.bind(this));
    //core.mapCmd("revert", this.revert.bind(this));

    core.on("setServicesCmd", this.processPending.bind(this));
  }

  save() {
    this.states.push(this.lastState);
  }

  revert() {
    this.toggleSelectedThemes(this.states.pop())
  }


  processPending() {
    let self = this;
    this.pendingConfiguration.forEach(item => {      
      item.fn.apply(self, item.params);
    });
  }

  init() {}

  addLayerCategories(categories) {
    let self = this;
    let core = this.core;
    let map = core.getMap();
    let groups = categories.map(category => {
      // build out all the layers, none will be visible yet
      let layers = category.layers.map(layer => {
        return self.makeLayer.call(self, layer);
      });

      // show layers that are part of current selection
      // and hide ones that are not part of current selection
      self.setLayerVisibilities(category.selection, layers);

      // group category layers into a layer group
      let group = new LayerGroup({
        opacity: category.opacity,
        layers: layers
      });

      layers.map(layer =>
        {
          if(!self.layers)
            self.layers = {};
          if(!self.layers[layer.get("id")])
            self.layers[layer.get("id")] = [];
            self.layers[layer.get("id")].push(layer);
        });

      group.set('id', category.category_key);      
      return group;
    });
    groups.forEach(group => map.addLayer(group));
  }

  updateLayer(data) {
    var newLayer = this.makeLayer(data)
    var layer = this.getLayerByKey(data.key)[0];
    layer.setSource(newLayer.getSource());
  }

  getLayerByKey(key)
  {
    return this.layers[key];
  }

  toggleSelectedThemes(data) {
    if(!data)
      return;
    let self = this;
    let map = this.core.getMap();
    data.forEach(datum => {
      let category = 
        map
          .getLayers()
          .getArray()
          .find(l => l.get('id') === datum.category_key);

      let layers = category.getLayers().getArray();

      self.setLayerVisibilities(datum.selection, layers);
    });

    this.lastState = data;
  }

  setLayerVisibilities(selection, layers) {
    let toggleLayer = function(layer, isMatch) {
      if (layer instanceof LayerGroup) {
        layer.setVisible(isMatch);
        layer.getLayers().getArray().forEach(child => child.setVisible(isMatch));
      } else {
        layer.setVisible(isMatch);
      }
    };

    switch (selection.selection_type) {
      case "monoselection":
        layers.forEach(layer => {
          toggleLayer(layer, selection.selection_key === layer.get('id'));
        });  
        break;

      case "polyselection":
        layers.forEach(layer => {
          toggleLayer(layer, selection.selection_keys.includes(layer.get('id')));
        });
        break;

      default:
        break;
    }
  }

  setCategoryTransparency(data) {
    let map = this.core.getMap();
    let category = 
        map
          .getLayers()
          .getArray()
          .find(l => l.get('id') === data.category_key);
    if (category) {
      category.setOpacity(data.transparency);
    }    
  }

  makeLayer(data) {
    // finalizes layer as either a layer group if it has multiple
    // endpoints or as a single layer if it only has one endpoint
    let groupLayers = function(layers) {
      if (layers.length > 1) {
        let group = new LayerGroup({ layers: layers });
        group.set('id', data.key);
        return group;
      } else if (layers.length === 1) {
        layers[0].set('id', data.key);
        return layers[0];
      } else {
        throw new Error(`Could not make layer for ${data.key}`);
      }
    };

    try {
      let self = this;
      let core = this.core;
      let layers = null;

      switch (data.config.type) {
        case "xyz":
          layers = data.config.value.endpoints.map(endpoint => {
            let lyr =  new TileLayer({
              visible: false,
              preload: 4,
              zIndex: endpoint.zIndex || 0,
              opacity: data.opacity || 1,
              source: new XYZ({
                crossOrigin: 'anonymous',
                url: endpoint.url,
                maxZoom: data.config.value.maxZoom || 26,
                minZoom: data.config.value.minZoom || 1,
                tileLoadFunction: (imageTile, src) => {
                  imageTile.getImage().src = src;
                }
              })   
            });
            lyr.set('id', data.key);
            lyr.set('name', data.name);
            return lyr;
          });

          return groupLayers(layers);
  
        case "wmts":
          var projection = proj.get("EPSG:3857"),
            projectionExtent = projection.getExtent(),
            size = extent.getWidth(projectionExtent) / 256,
            zooms = 15 + 1,
            resolutions = new Array(zooms),
            matrixIds = new Array(zooms);
          for (let z = 0; z < zooms; ++z) {
            resolutions[z] = size / Math.pow(2, z);
            matrixIds[z] = z;
          }
          
          layers = data.config.value.endpoints.map(endpoint => {
            let source = new WMTS({
              crossOrigin: 'anonymous',
              matrixSet: 'webmercator',
              format: 'image/png',
              projection: projection,
              requestEncoding: 'REST',
              tileGrid: new WMTSTileGrid({
                extent: data.config.value.extent,
                resolutions: resolutions,
                matrixIds: matrixIds
              }),
              style: 'default',
              opaque: false,
              transparent: true
            });

            let configureSource = function (tokenKey) {
              if (core.services && core.services[tokenKey]) {
                let tokenData = core.services[tokenKey];
                source.setUrl(`${tokenData.baseUrl || ""}${endpoint.url}`);
                source.setTileLoadFunction(function (imageTile, src) {
                  imageTile.getImage().src = `${src}?token=${tokenData.token || ""}`;
                });
              }
            }

            if (endpoint.tokenKey) {
              // if the token data has already been fetched and stored in core.services
              // go ahead and configure the source w/ the data, otherwise, postpone
              // the configuration until `setServicesCmd` has been triggered
              if (core.services && core.services[endpoint.tokenKey]) {
                configureSource(endpoint.tokenKey);
              } else {
                self.pendingConfiguration.push({
                  name: data.key,
                  fn: configureSource,
                  params: [ endpoint.tokenKey ]
                });
              }
            }

            let lyr = new TileLayer({
              visible: false,
              preload: 4,
              zIndex: endpoint.zIndex || 0,
              opacity: data.opacity || 1,
              source: source,
              opaque: false
            });
            lyr.set('id', data.key);
            lyr.set('name', data.name);
            return lyr;
          });

          return groupLayers(layers);

        case "wms":
          var projection = proj.get("EPSG:3857"),
              projectionExtent = projection.getExtent(),
              size = extent.getWidth(projectionExtent) / 256,
              zooms = 15 + 1,
              resolutions = new Array(zooms);
          for (let z = 0; z < zooms; ++z) {
            resolutions[z] = size / Math.pow(2, z);
          }

          layers = data.config.value.endpoints.map(endpoint => {
            //The random adds a random value to the parameter
            //essentually cache busting  
            let customParams = {
              get random() {
                return Math.random();
              }
            };

            let source = new ImageWMS({
              params: {'LAYERS': 'geonode:shapes'},
              ratio: 1,
              serverType: 'geoserver',
              resolutions: resolutions,
              projection: projection
            });

            let configureSource = function (tokenKey) {
              if (core.services && core.services[tokenKey]) {
                let tokenData = core.services[tokenKey];
                source.setUrl(`${tokenData.baseUrl || ""}${endpoint.url}`);
                if (tokenData.token) {
                  customParams["token"] = tokenData.token;
                }
                source.params_ = customParams;
              }
            }

            if (endpoint.tokenKey) {
              // if the token data has already been fetched and stored in core.services
              // go ahead and configure the source w/ the data, otherwise, postpone
              // the configuration until `setServicesCmd` has been triggered
              if (core.services && core.services[endpoint.tokenKey]) {
                configureSource(endpoint.tokenKey);
              } else {
                self.pendingConfiguration.push({
                  name: data.key,
                  fn: configureSource,
                  params: [ endpoint.tokenKey ]
                });
              }
            }

            let lyr = new ImageLayer({
              zIndex: endpoint.zIndex || 0,
              extent: data.config.value.extent,
              source: source
            });
            lyr.set('id', data.key);
            lyr.set('name', data.name);
            return lyr;
          })

          return groupLayers(laeyrs);
  
        case "esriExport":
          layers = data.config.value.endpoints.map(endpoint => {
            //The random adds a random value to the parameter
            //essentually cache busting  
            let customParams = {
              get random() {
                return Math.random();
              }
            };
  
            if (endpoint.bbox) {
              customParams["BBOX"] = endpoint.bbox;
            }
  
            if (endpoint.layersToShow) {
              customParams["LAYERS"] = endpoint.layersToShow;
            }
  
            if (endpoint.layerDefs) {
              customParams["LAYERDEFS"] = endpoint.layerDefs
            }

            let source = new ImageArcGISRest({
              crossOrigin: 'anonymous',
              ratio: 1,
              maxZoom: 26,
              tileLoadFunction: (image, src) => {
                image.getImage().src = src;
              }
            });

            let configureSource = function (tokenKey) {
              if (core.services && core.services[tokenKey]) {
                let tokenData = core.services[tokenKey];
                source.setUrl(`${tokenData.baseUrl || ""}${endpoint.url}`);
                if (tokenData.token) {
                  customParams["token"] = tokenData.token;
                }
                source.params_ = customParams;
              }
            }
  
            if (endpoint.tokenKey) {
              // if the token data has already been fetched and stored in core.services
              // go ahead and configure the source w/ the data, otherwise, postpone
              // the configuration until `setServicesCmd` has been triggered
              if (core.services && core.services[endpoint.tokenKey]) {
                configureSource(endpoint.tokenKey);
              } else {
                self.pendingConfiguration.push({
                  name: data.key,
                  fn: configureSource,
                  params: [ endpoint.tokenKey ]
                });
              }
            }
            else
            {
              source.setUrl(endpoint.url);
              source.parms_ = customParams;
            }
  
            let lyr = new ImageLayer({
              visible: false,
              preload: 4,
              zIndex: endpoint.zIndex || 0,
              opacity: data.opacity || 1,
              source: source,
              extent: data.config.value.extent
            });
            lyr.set('id', data.key);
            return lyr;
          });

          return groupLayers(layers);
  
        default: 
          throw new Error(`Layer type '${data.config.type}' has not been implemented.`);
      }
    }
    catch(err) {
      console.log(err);
    }
    
  }
  render() {}
}
